import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import Close from '@assets/icons/svgs/Close';
import {
  DATE_RANGE_PRESETS,
  DATE_RANGE_PRESET_LABELS,
  DOCUMENT_STATUS_LABELS,
  FILE_FORMAT_LABELS,
} from '@utils/documentVault';
import { DocumentVaultFilterContext, VAULT_FACETS } from '@/contexts/DocumentVaultFilterContext';

const ChipsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;

  padding: 4px 10px;

  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;

// Matches the shared selected-tag chip: same icon, same 12px size, same stop-at-the-icon hit area.
const RemoveChipButton = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    height: 8px;
    width: 12px;
  }

  path {
    fill: ${({ theme }) => theme.themeColors.secondaryText};
  }
`;

function buildChips(filters, handlers) {
  const chips = [];

  if (filters.search) {
    chips.push({ key: 'search', label: `“${filters.search}”`, onRemove: handlers.clearSearch });
  }

  const facetChips = [
    { facet: VAULT_FACETS.tickers, values: filters.tickers, labelFor: (v) => v },
    { facet: VAULT_FACETS.docTypes, values: filters.docTypes, labelFor: (v) => v },
    { facet: VAULT_FACETS.tags, values: filters.tags, labelFor: (v) => v },
    {
      facet: VAULT_FACETS.statuses,
      values: filters.statuses,
      labelFor: (v) => DOCUMENT_STATUS_LABELS[v] || v,
    },
    {
      facet: VAULT_FACETS.fileFormats,
      values: filters.fileFormats,
      labelFor: (v) => FILE_FORMAT_LABELS[v] || v,
    },
  ];

  facetChips.forEach(({ facet, values, labelFor }) => {
    (values || []).forEach((value) => {
      chips.push({
        key: `${facet}:${value}`,
        label: labelFor(value),
        onRemove: () => handlers.toggleFacetValue(facet, value),
      });
    });
  });

  if (filters.dateRange?.preset && filters.dateRange.preset !== DATE_RANGE_PRESETS.all) {
    const isCustom = filters.dateRange.preset === DATE_RANGE_PRESETS.custom;
    const customLabel = [filters.dateRange.from, filters.dateRange.to].filter(Boolean).join(' – ');
    chips.push({
      key: 'dateRange',
      label: isCustom && customLabel ? customLabel : DATE_RANGE_PRESET_LABELS[filters.dateRange.preset],
      onRemove: handlers.clearDateRange,
    });
  }

  if (filters.cleanEligibleOnly) {
    chips.push({ key: 'cleanEligibleOnly', label: 'Clean-copy eligible', onRemove: handlers.clearCleanEligible });
  }

  return chips;
}

export const VaultFilterChips = () => {
  const { filters, toggleFacetValue, setSearch, setDateRange, setCleanEligibleOnly, clearAll } =
    React.useContext(DocumentVaultFilterContext);

  const chips = buildChips(filters, {
    toggleFacetValue,
    clearSearch: () => setSearch(''),
    clearDateRange: () => setDateRange({ preset: DATE_RANGE_PRESETS.all, from: null, to: null }),
    clearCleanEligible: () => setCleanEligibleOnly(false),
  });

  if (chips.length === 0) return null;

  return (
    <Container top={12}>
      <ChipsWrapper>
        {chips.map((chip) => (
          <Chip key={chip.key}>
            <Body5>{chip.label}</Body5>
            <RemoveChipButton onClick={chip.onRemove} aria-label={`Remove ${chip.label} filter`}>
              <Close width={12} />
            </RemoveChipButton>
          </Chip>
        ))}
        <SkeletonButton onClick={clearAll}>Clear all</SkeletonButton>
      </ChipsWrapper>
    </Container>
  );
};
